var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { formatDateForAPI, getDefaultDateRange } from "../SalesOrderDetails/STReports/DateRangePicker/dateRangeFormatUtils";
import { parseDisplayDateValue } from "../SharedFunctions";
import { logClientRuntimeError } from "../../services/clientErrorLog";
import { getSTOrderDetailsRefundRowsFromPayload, mergeRefundOrdersIntoAllTabOrders, parseStReportsRefundGridRow, } from "./stOrderDetailsRefundGrid";
export { classifySTOrderDetailsOrderRow, getSTOrderDetailsStatusChipBgColor, getSTOrderDetailsStatusDisplayLabel, isSTOrderDetailsRefundOrderNoClickable, normalizeSTOrderDetailsServerStatus, } from "./stOrderDetailsOrderStatus";
/** Main navigation tab index for Sales-Order Details (`Tabs.tsx`). */
export var SALES_ORDER_DETAILS_MAIN_TAB_INDEX = 9;
/** Fixed page size for Sales Order Details grid and GET `data=59` (must match server `pageSize`). */
export var ST_ORDER_DETAILS_PAGE_SIZE = 50;
export var ST_ORDER_DETAILS_ROWS_PER_PAGE_OPTIONS = [ST_ORDER_DETAILS_PAGE_SIZE];
export var ST_ORDER_DETAILS_DEFAULT_ROWS_PER_PAGE = ST_ORDER_DETAILS_PAGE_SIZE;
/** Debounce before Case 59 `searchQuery` API calls while typing in Sales Order Details search. */
export var ST_ORDER_DETAILS_SEARCH_DEBOUNCE_MS = 4000;
/**
 * Maps trimmed search text to `stReportsSalesOrderDetailsRequest.searchQuery` (omit when empty).
 * Values are URL-encoded in `appendStReportsSalesOrderDetailsQuerySegment` via `encodeURIComponent`.
 */
export function buildSTOrderDetailsSearchQueryParam(searchText) {
    var searchQuery = searchText.trim();
    return searchQuery ? { searchQuery: searchQuery } : {};
}
/** UI tabs aligned with `StReportsOrdersResponse.tabOrderSlices` keys (excluding `All` prefix case). */
export var ST_ORDER_DETAILS_UI_TAB_STATUSES = ["All", "Paid", "Unpaid", "Draft", "Refunded"];
export var SALES_ORDER_DETAILS_CSV_EXPORT_TAB_LABEL_KEYS = {
    All: "all",
    Paid: "paid",
    Unpaid: "unpaid",
    Draft: "draft",
    Refunded: "refunded",
};
/** CSV export columns (invoice excluded by product requirement). */
export var SALES_ORDER_DETAILS_ORDER_EXPORT_COLUMN_KEYS = [
    "orderNumber",
    "customerName",
    "orderDate",
    "orderPaidDate",
    "subtotal",
    "commission",
    "companyName",
    "companyLocation",
    "salesMethod",
    "fulfilmentStatus",
    "status",
];
/** CSV export columns for Refund tab (`tabOrderSlices.Refunded.RefundOrders`). */
export var SALES_ORDER_DETAILS_REFUND_EXPORT_COLUMN_KEYS = [
    "refundId",
    "createdDate",
    "orderNo",
    "commission",
    "teamManagerCommission",
    "refundSubTotal",
    "orderStatus",
    "salesMethod",
];
export function createSTOrderDetailsInitialTabPages() {
    return ST_ORDER_DETAILS_UI_TAB_STATUSES.reduce(function (acc, k) {
        acc[k] = 0;
        return acc;
    }, {});
}
/** Zero-based max page index for MUI `TablePagination` (`page` prop). */
export function getSTOrderDetailsMaxPageIndex(totalCount, pageSize) {
    if (!Number.isFinite(totalCount) || totalCount <= 0 || !Number.isFinite(pageSize) || pageSize < 1) {
        return 0;
    }
    return Math.max(0, Math.ceil(totalCount / pageSize) - 1);
}
/** Clamp a tab page index to `[0, maxPage]` for the tab's `totalCount`. */
export function clampSTOrderDetailsPageIndex(page, totalCount, pageSize) {
    var maxPage = getSTOrderDetailsMaxPageIndex(totalCount, pageSize);
    if (!Number.isFinite(page) || page < 0) {
        return 0;
    }
    return Math.min(page, maxPage);
}
/** MVC GET prefix for Sales Order Details / ST Reports list (`StReportsSalesOrderDetailsRequest`). */
export var ST_REPORTS_SALES_ORDER_DETAILS_PREFIX = "stReportsSalesOrderDetailsRequest";
export function STOrderDetailsTabSliceKeyFromUiTab(ui) {
    var u = String(ui || "All").toLowerCase();
    if (u === "all")
        return "all";
    if (u === "paid")
        return "paid";
    if (u === "unpaid")
        return "unpaid";
    if (u === "draft")
        return "draft";
    if (u === "refunded")
        return "refunded";
    return "all";
}
var REQUEST_KEYS = [
    "dateFrom",
    "dateTo",
    "dateField",
    "pageNumber",
    "pageSize",
    "activeTab",
    "searchQuery",
    "status",
    "countOnly",
    "exportCsv",
];
function shouldEmitQueryValue(v) {
    if (v === undefined || v === null) {
        return false;
    }
    if (typeof v === "string" && v === "") {
        return false;
    }
    if (typeof v === "boolean" && v === false) {
        return false;
    }
    return true;
}
export function stReportsSalesOrderDetailsToFlatQueryParams(req) {
    var out = {};
    var P = ST_REPORTS_SALES_ORDER_DETAILS_PREFIX;
    for (var _i = 0, REQUEST_KEYS_1 = REQUEST_KEYS; _i < REQUEST_KEYS_1.length; _i++) {
        var k = REQUEST_KEYS_1[_i];
        var v = req[k];
        if (!shouldEmitQueryValue(v)) {
            continue;
        }
        out["".concat(P, ".").concat(String(k))] = String(v);
    }
    return out;
}
export function appendStReportsSalesOrderDetailsQuerySegment(req) {
    var flat = stReportsSalesOrderDetailsToFlatQueryParams(req);
    var qs = "&data=".concat(59);
    for (var _i = 0, _a = Object.entries(flat); _i < _a.length; _i++) {
        var _b = _a[_i], k = _b[0], v = _b[1];
        qs += "&".concat(k, "=").concat(encodeURIComponent(v));
    }
    return qs;
}
function normalizeOrderTabOrderSlice(raw) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (!raw || typeof raw !== "object") {
        return null;
    }
    var s = raw;
    var ordersRaw = (_a = s.orders) !== null && _a !== void 0 ? _a : s.Orders;
    return {
        orders: Array.isArray(ordersRaw) ? ordersRaw : [],
        totalCount: Number((_c = (_b = s.totalCount) !== null && _b !== void 0 ? _b : s.TotalCount) !== null && _c !== void 0 ? _c : 0),
        pageNumber: Number((_e = (_d = s.pageNumber) !== null && _d !== void 0 ? _d : s.PageNumber) !== null && _e !== void 0 ? _e : 1),
        pageSize: Number((_g = (_f = s.pageSize) !== null && _f !== void 0 ? _f : s.PageSize) !== null && _g !== void 0 ? _g : 0),
        hasNextPage: Boolean((_h = s.hasNextPage) !== null && _h !== void 0 ? _h : s.HasNextPage),
    };
}
function normalizeRefundTabOrderSlice(raw) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var s = raw && typeof raw === "object" ? raw : {};
    var refundOrdersRaw = (_a = s.refundOrders) !== null && _a !== void 0 ? _a : s.RefundOrders;
    return {
        refundOrders: Array.isArray(refundOrdersRaw)
            ? refundOrdersRaw.map(parseStReportsRefundGridRow)
            : [],
        totalCount: Number((_c = (_b = s.totalCount) !== null && _b !== void 0 ? _b : s.TotalCount) !== null && _c !== void 0 ? _c : 0),
        pageNumber: Number((_e = (_d = s.pageNumber) !== null && _d !== void 0 ? _d : s.PageNumber) !== null && _e !== void 0 ? _e : 1),
        pageSize: Number((_g = (_f = s.pageSize) !== null && _f !== void 0 ? _f : s.PageSize) !== null && _g !== void 0 ? _g : 0),
        hasNextPage: Boolean((_h = s.hasNextPage) !== null && _h !== void 0 ? _h : s.HasNextPage),
    };
}
function emptyTabOrderSlice() {
    return {
        orders: [],
        totalCount: 0,
        pageNumber: 1,
        pageSize: 0,
        hasNextPage: false,
    };
}
function sliceHasRowsForTab(slices, tabKey) {
    if (tabKey === "refunded") {
        return Array.isArray(slices.refunded.refundOrders) && slices.refunded.refundOrders.length > 0;
    }
    var slice = slices[tabKey];
    return Array.isArray(slice.orders) && slice.orders.length > 0;
}
function normalizeTabOrderSlices(raw) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (!raw || typeof raw !== "object") {
        return null;
    }
    var r = raw;
    var all = normalizeOrderTabOrderSlice((_a = r.all) !== null && _a !== void 0 ? _a : r.All);
    if (!all) {
        return null;
    }
    var slices = {
        all: all,
        paid: (_c = normalizeOrderTabOrderSlice((_b = r.paid) !== null && _b !== void 0 ? _b : r.Paid)) !== null && _c !== void 0 ? _c : emptyTabOrderSlice(),
        unpaid: (_e = normalizeOrderTabOrderSlice((_d = r.unpaid) !== null && _d !== void 0 ? _d : r.Unpaid)) !== null && _e !== void 0 ? _e : emptyTabOrderSlice(),
        draft: (_g = normalizeOrderTabOrderSlice((_f = r.draft) !== null && _f !== void 0 ? _f : r.Draft)) !== null && _g !== void 0 ? _g : emptyTabOrderSlice(),
        refunded: normalizeRefundTabOrderSlice((_h = r.refunded) !== null && _h !== void 0 ? _h : r.Refunded),
    };
    return slices;
}
export function normalizeStReportsOrdersApiPayload(raw) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
    if (!raw || typeof raw !== "object") {
        return false;
    }
    var r = raw;
    var tabOrderSlices = normalizeTabOrderSlices((_a = r.tabOrderSlices) !== null && _a !== void 0 ? _a : r.TabOrderSlices);
    if (!tabOrderSlices) {
        return false;
    }
    var exportOrderBuckets = (_c = (_b = r.exportOrderBuckets) !== null && _b !== void 0 ? _b : r.ExportOrderBuckets) !== null && _c !== void 0 ? _c : null;
    var allOrders = tabOrderSlices.all.orders;
    return __assign(__assign({}, r), { Orders: allOrders, orders: allOrders, TotalCount: (_d = r.totalCount) !== null && _d !== void 0 ? _d : r.TotalCount, TotalSubtotal: (_e = r.totalSubtotal) !== null && _e !== void 0 ? _e : r.TotalSubtotal, TotalCommission: (_f = r.totalCommission) !== null && _f !== void 0 ? _f : r.TotalCommission, Currency: (_g = r.currency) !== null && _g !== void 0 ? _g : r.Currency, refundTotalCount: (_j = (_h = r.refundTotalCount) !== null && _h !== void 0 ? _h : r.RefundTotalCount) !== null && _j !== void 0 ? _j : tabOrderSlices.refunded.totalCount, RefundTotalCount: (_l = (_k = r.refundTotalCount) !== null && _k !== void 0 ? _k : r.RefundTotalCount) !== null && _l !== void 0 ? _l : tabOrderSlices.refunded.totalCount, hasNextPage: (_m = r.hasNextPage) !== null && _m !== void 0 ? _m : r.HasNextPage, pageNumber: (_o = r.pageNumber) !== null && _o !== void 0 ? _o : r.PageNumber, pageSize: (_p = r.pageSize) !== null && _p !== void 0 ? _p : r.PageSize, tabOrderSlices: tabOrderSlices, TabOrderSlices: tabOrderSlices, exportOrderBuckets: exportOrderBuckets, ExportOrderBuckets: exportOrderBuckets, activeTab: (_r = (_q = r.activeTab) !== null && _q !== void 0 ? _q : r.ActiveTab) !== null && _r !== void 0 ? _r : null, ActiveTab: (_t = (_s = r.activeTab) !== null && _s !== void 0 ? _s : r.ActiveTab) !== null && _t !== void 0 ? _t : null, paidCount: (_u = r.paidCount) !== null && _u !== void 0 ? _u : r.PaidCount, unpaidCount: (_v = r.unpaidCount) !== null && _v !== void 0 ? _v : r.UnpaidCount, paymentPendingCount: (_w = r.paymentPendingCount) !== null && _w !== void 0 ? _w : r.PaymentPendingCount, draftCount: (_x = r.draftCount) !== null && _x !== void 0 ? _x : r.DraftCount, refundedCount: (_y = r.refundedCount) !== null && _y !== void 0 ? _y : r.RefundedCount });
}
/** Case 59 payload ready for Redux / date-range cache (same as `stReportsDataService` output). */
export function prepareSTOrderDetailsReportsPayload(raw) {
    var norm = normalizeStReportsOrdersApiPayload(raw);
    return norm !== false ? norm : null;
}
/** When the API scopes the payload to a tab, `activeTab` / `ActiveTab` should match `uiTab` before using root `Orders` / `TotalCount` instead of an empty slice. */
function stOrderDetailsResponseTabMatchesUi(reportsData, uiTab) {
    var _a, _b;
    var a = String((_b = (_a = reportsData === null || reportsData === void 0 ? void 0 : reportsData.activeTab) !== null && _a !== void 0 ? _a : reportsData === null || reportsData === void 0 ? void 0 : reportsData.ActiveTab) !== null && _b !== void 0 ? _b : "").trim().toLowerCase();
    if (!a) {
        return true;
    }
    var u = String(uiTab || "All").trim().toLowerCase();
    if (u === "all") {
        return a === "all";
    }
    return a === u;
}
/**
 * Merges a paginated Case 59 response into the previous payload so tab switches keep other
 * `tabOrderSlices` buckets. `activeTab: "All"` replaces all slices; other tabs update one slice.
 */
export function mergeStReportsOrdersApiPayload(previous, incoming, requestActiveTab, options) {
    var _a;
    var _b, _c;
    var nextNorm = normalizeStReportsOrdersApiPayload(incoming);
    if (!nextNorm) {
        return incoming;
    }
    /** Search responses are authoritative — never blend with date-range rows in other tabs. */
    if (options === null || options === void 0 ? void 0 : options.isSearchMode) {
        return nextNorm;
    }
    var prevNorm = previous ? normalizeStReportsOrdersApiPayload(previous) : false;
    if (prevNorm === false) {
        return nextNorm;
    }
    var prevSlices = getSTOrderDetailsTabSlicesFromPayload(prevNorm);
    var nextSlices = getSTOrderDetailsTabSlicesFromPayload(nextNorm);
    if (!prevSlices || !nextSlices) {
        return nextNorm;
    }
    var activeTab = String((_c = (_b = requestActiveTab !== null && requestActiveTab !== void 0 ? requestActiveTab : nextNorm.activeTab) !== null && _b !== void 0 ? _b : nextNorm.ActiveTab) !== null && _c !== void 0 ? _c : "All").trim();
    var activeLower = activeTab.toLowerCase();
    var activeKey = STOrderDetailsTabSliceKeyFromUiTab(activeTab);
    var pickMergedOrderSlice = function (key) {
        var _a;
        var next = nextSlices[key];
        var prev = prevSlices[key];
        /** Tab-specific pagination — only the requested tab changes; never overwrite other tabs from the response. */
        if (activeLower !== "all") {
            return activeKey === key ? next : prev;
        }
        var nextHasData = ((_a = next === null || next === void 0 ? void 0 : next.totalCount) !== null && _a !== void 0 ? _a : 0) > 0 || sliceHasRowsForTab(nextSlices, key);
        if (nextHasData) {
            return next;
        }
        return prev;
    };
    var pickMergedRefundSlice = function () {
        var _a;
        var next = nextSlices.refunded;
        var prev = prevSlices.refunded;
        if (activeLower !== "all") {
            return activeLower === "refunded" ? next : prev;
        }
        var nextHasData = ((_a = next === null || next === void 0 ? void 0 : next.totalCount) !== null && _a !== void 0 ? _a : 0) > 0 || sliceHasRowsForTab(nextSlices, "refunded");
        if (nextHasData) {
            return next;
        }
        return prev;
    };
    var mergedSlices;
    if (activeLower === "all") {
        mergedSlices = {
            all: pickMergedOrderSlice("all"),
            paid: pickMergedOrderSlice("paid"),
            unpaid: pickMergedOrderSlice("unpaid"),
            draft: pickMergedOrderSlice("draft"),
            refunded: pickMergedRefundSlice(),
        };
    }
    else {
        var k = activeKey;
        if (k === "refunded") {
            mergedSlices = __assign(__assign({}, prevSlices), { refunded: pickMergedRefundSlice() });
        }
        else {
            mergedSlices = __assign(__assign({}, prevSlices), (_a = {}, _a[k] = pickMergedOrderSlice(k), _a));
        }
    }
    return __assign(__assign({}, nextNorm), { tabOrderSlices: mergedSlices, TabOrderSlices: mergedSlices, refundTotalCount: mergedSlices.refunded.totalCount, RefundTotalCount: mergedSlices.refunded.totalCount });
}
export function getSTOrderDetailsTabSlicesFromPayload(reportsData) {
    var _a;
    var s = (_a = reportsData === null || reportsData === void 0 ? void 0 : reportsData.tabOrderSlices) !== null && _a !== void 0 ? _a : reportsData === null || reportsData === void 0 ? void 0 : reportsData.TabOrderSlices;
    return normalizeTabOrderSlices(s);
}
export function getSTOrderDetailsOrdersForUiTab(reportsData, uiTab) {
    var _a;
    if (String(uiTab || "").trim().toLowerCase() === "refunded") {
        return [];
    }
    var slices = getSTOrderDetailsTabSlicesFromPayload(reportsData);
    if (slices) {
        var k = STOrderDetailsTabSliceKeyFromUiTab(uiTab);
        if (k !== "refunded") {
            var orders = slices[k].orders;
            if (Array.isArray(orders)) {
                if (k === "all") {
                    return mergeRefundOrdersIntoAllTabOrders(orders, getSTOrderDetailsRefundRowsFromPayload(reportsData));
                }
                return orders;
            }
        }
        return [];
    }
    var top = (_a = reportsData === null || reportsData === void 0 ? void 0 : reportsData.Orders) !== null && _a !== void 0 ? _a : reportsData === null || reportsData === void 0 ? void 0 : reportsData.orders;
    if (Array.isArray(top)) {
        if (String(uiTab || "").trim().toLowerCase() === "all") {
            return mergeRefundOrdersIntoAllTabOrders(top, getSTOrderDetailsRefundRowsFromPayload(reportsData));
        }
        return top;
    }
    return [];
}
export function getSTOrderDetailsTotalCountForUiTab(reportsData, uiTab) {
    var _a, _b, _c;
    var rawRoot = (_a = reportsData === null || reportsData === void 0 ? void 0 : reportsData.TotalCount) !== null && _a !== void 0 ? _a : reportsData === null || reportsData === void 0 ? void 0 : reportsData.totalCount;
    var rootCount = rawRoot != null && Number.isFinite(Number(rawRoot)) && Number(rawRoot) >= 0 ? Number(rawRoot) : null;
    var slices = getSTOrderDetailsTabSlicesFromPayload(reportsData);
    if (slices) {
        var k = STOrderDetailsTabSliceKeyFromUiTab(uiTab);
        if (k === "all") {
            var allTotal = Number((_c = (_b = slices.all) === null || _b === void 0 ? void 0 : _b.totalCount) !== null && _c !== void 0 ? _c : NaN);
            if (Number.isFinite(allTotal) && allTotal >= 0) {
                return allTotal;
            }
            if (rootCount != null && stOrderDetailsResponseTabMatchesUi(reportsData, uiTab)) {
                return rootCount;
            }
            var merged = mergeRefundOrdersIntoAllTabOrders(slices.all.orders, getSTOrderDetailsRefundRowsFromPayload(reportsData));
            return merged.length;
        }
        var slice = k === "refunded" ? slices.refunded : slices[k];
        if (slice && slice.totalCount != null) {
            var n_1 = Number(slice.totalCount);
            if (Number.isFinite(n_1) && n_1 >= 0) {
                return n_1;
            }
        }
        if (rootCount != null && stOrderDetailsResponseTabMatchesUi(reportsData, uiTab)) {
            return rootCount;
        }
        return 0;
    }
    if (rawRoot == null) {
        return null;
    }
    var n = Number(rawRoot);
    return Number.isFinite(n) && n >= 0 ? n : null;
}
/** True when cached `tabOrderSlices` data matches the requested zero-based page index. */
export function isSTOrderDetailsSliceCachedForPage(reportsData, uiTab, pageIndexZeroBased, pageSize) {
    var _a;
    var slices = getSTOrderDetailsTabSlicesFromPayload(reportsData);
    if (!slices) {
        return false;
    }
    var tabKey = STOrderDetailsTabSliceKeyFromUiTab(uiTab);
    var slice = tabKey === "refunded" ? slices.refunded : slices[tabKey];
    if (!slice) {
        return false;
    }
    var expectedPage = pageIndexZeroBased + 1;
    var slicePage = Number(slice.pageNumber);
    var sliceSize = Number(slice.pageSize);
    if (!Number.isFinite(slicePage) || slicePage !== expectedPage) {
        return false;
    }
    if (Number.isFinite(sliceSize) && sliceSize > 0 && sliceSize !== pageSize) {
        return false;
    }
    var rows = tabKey === "refunded"
        ? Array.isArray(slice.refundOrders)
            ? slice.refundOrders
            : []
        : Array.isArray(slice.orders)
            ? slice.orders
            : [];
    var total = Number((_a = slice.totalCount) !== null && _a !== void 0 ? _a : 0);
    if (rows.length > 0) {
        return true;
    }
    return Number.isFinite(total) && total === 0;
}
/** True when the active UI tab has no rows to render (null slices + empty root list, or empty tab slice). */
export function isSTOrderDetailsActiveTabEmpty(reportsData, uiTab) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (!reportsData || typeof reportsData !== "object") {
        return false;
    }
    var slices = getSTOrderDetailsTabSlicesFromPayload(reportsData);
    if (slices == null) {
        var top_1 = (_a = reportsData.Orders) !== null && _a !== void 0 ? _a : reportsData.orders;
        return !Array.isArray(top_1) || top_1.length === 0;
    }
    var tabKey = STOrderDetailsTabSliceKeyFromUiTab(uiTab);
    if (tabKey === "all") {
        var total_1 = Number((_c = (_b = slices.all) === null || _b === void 0 ? void 0 : _b.totalCount) !== null && _c !== void 0 ? _c : 0);
        if (Number.isFinite(total_1) && total_1 > 0) {
            return false;
        }
        var merged = mergeRefundOrdersIntoAllTabOrders(slices.all.orders, getSTOrderDetailsRefundRowsFromPayload(reportsData));
        return merged.length === 0;
    }
    var total = tabKey === "refunded"
        ? Number((_e = (_d = slices.refunded) === null || _d === void 0 ? void 0 : _d.totalCount) !== null && _e !== void 0 ? _e : 0)
        : Number((_g = (_f = slices[tabKey]) === null || _f === void 0 ? void 0 : _f.totalCount) !== null && _g !== void 0 ? _g : 0);
    if (Number.isFinite(total) && total > 0) {
        return false;
    }
    return !sliceHasRowsForTab(slices, tabKey);
}
/** In-memory payload already contains the requested tab/page (no API). */
export function hasInMemorySTOrderDetailsSliceForView(reportsData, uiTab, pageIndexZeroBased, pageSize) {
    if (!reportsData) {
        return false;
    }
    if (isSTOrderDetailsSliceCachedForPage(reportsData, uiTab, pageIndexZeroBased, pageSize)) {
        return true;
    }
    /** Bootstrap `All` fetch only satisfies the All tab at page 0 — not Paid/Unpaid/Draft/Refunded slices. */
    var uiTabLower = String(uiTab || "").trim().toLowerCase();
    if (uiTabLower === "all" && pageIndexZeroBased === 0 && getSTOrderDetailsTabSlicesFromPayload(reportsData) != null) {
        return isSTOrderDetailsSliceCachedForPage(reportsData, "All", 0, pageSize);
    }
    return false;
}
var TAB_ORDER_SLICE_KEYS = ["all", "paid", "unpaid", "draft", "refunded"];
/** True when Case 59 `tabOrderSlices` (or root list) has zero orders across all status buckets. */
export function isSTOrderDetailsPayloadEmpty(reportsData) {
    var _a, _b, _c;
    if (!reportsData || typeof reportsData !== "object") {
        return false;
    }
    var slices = getSTOrderDetailsTabSlicesFromPayload(reportsData);
    if (slices) {
        return TAB_ORDER_SLICE_KEYS.every(function (k) {
            var _a, _b;
            var total = k === "refunded" ? Number((_a = slices.refunded.totalCount) !== null && _a !== void 0 ? _a : 0) : Number((_b = slices[k].totalCount) !== null && _b !== void 0 ? _b : 0);
            if (Number.isFinite(total) && total > 0) {
                return false;
            }
            return !sliceHasRowsForTab(slices, k);
        });
    }
    var refundTotal = Number((_b = (_a = reportsData.refundTotalCount) !== null && _a !== void 0 ? _a : reportsData.RefundTotalCount) !== null && _b !== void 0 ? _b : 0);
    if (Number.isFinite(refundTotal) && refundTotal > 0) {
        return false;
    }
    var raw = (_c = reportsData.TotalCount) !== null && _c !== void 0 ? _c : reportsData.totalCount;
    if (raw != null && Number.isFinite(Number(raw))) {
        return Number(raw) <= 0;
    }
    return true;
}
/** True when an applied date range should show the picker "no data for period" footer message. */
export function isNoDataForSTOrderDetailsAppliedRange(dateRange, reportsData) {
    if (!(dateRange === null || dateRange === void 0 ? void 0 : dateRange.startDate) || !(dateRange === null || dateRange === void 0 ? void 0 : dateRange.endDate) || reportsData == null) {
        return false;
    }
    return isSTOrderDetailsPayloadEmpty(reportsData);
}
function normalizeCalendarDay(date) {
    var d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}
/** Case 59 order rows use `MM/dd/yyyy` (optional time); treat `-` / `null` as empty. */
export function isMeaningfulSTOrderDetailsDateValue(raw) {
    try {
        var s = raw == null ? "" : String(raw).trim();
        return s !== "" && s !== "-" && s.toLowerCase() !== "null";
    }
    catch (error) {
        logClientRuntimeError("isMeaningfulSTOrderDetailsDateValue", error);
        return false;
    }
}
/** Paid date when set; otherwise order date (`MM/dd/yyyy` string from API). */
export function getSTOrderDetailsOrderEffectiveDateRaw(row) {
    var _a, _b;
    try {
        var paid = (_a = row.OrderPaidDate) !== null && _a !== void 0 ? _a : row.orderPaidDate;
        if (isMeaningfulSTOrderDetailsDateValue(paid)) {
            return String(paid).trim();
        }
        var orderDate = (_b = row.OrderDate) !== null && _b !== void 0 ? _b : row.orderDate;
        if (isMeaningfulSTOrderDetailsDateValue(orderDate)) {
            return String(orderDate).trim();
        }
        return null;
    }
    catch (error) {
        logClientRuntimeError("getSTOrderDetailsOrderEffectiveDateRaw", error);
        return null;
    }
}
/** True when the order's effective date falls within the inclusive calendar range. */
export function isSTOrderDetailsOrderInCalendarRange(row, rangeStart, rangeEnd) {
    try {
        var raw = getSTOrderDetailsOrderEffectiveDateRaw(row);
        if (!raw) {
            return false;
        }
        var parsed = parseDisplayDateValue(raw);
        if (!parsed) {
            return false;
        }
        var day = normalizeCalendarDay(parsed);
        var start = normalizeCalendarDay(rangeStart);
        var end = normalizeCalendarDay(rangeEnd);
        return day >= start && day <= end;
    }
    catch (error) {
        logClientRuntimeError("isSTOrderDetailsOrderInCalendarRange", error);
        return false;
    }
}
/** Deep-clone Case 59 payload so working `stReportsData` edits do not mutate the bootstrap cache. */
export function cloneSTOrderDetailsReportsPayload(payload) {
    if (payload == null) {
        return payload;
    }
    return JSON.parse(JSON.stringify(payload));
}
export function getSTOrderDetailsDefaultDateRangeApiKeys() {
    var dr = getDefaultDateRange();
    return {
        dateFrom: formatDateForAPI(dr.startDate),
        dateTo: formatDateForAPI(dr.endDate),
    };
}
export function isSTOrderDetailsDefaultDateRange(dateRange) {
    if (!(dateRange === null || dateRange === void 0 ? void 0 : dateRange.startDate) || !(dateRange === null || dateRange === void 0 ? void 0 : dateRange.endDate)) {
        return false;
    }
    var def = getDefaultDateRange();
    return (normalizeCalendarDay(dateRange.startDate) === normalizeCalendarDay(def.startDate) &&
        normalizeCalendarDay(dateRange.endDate) === normalizeCalendarDay(def.endDate));
}
/** True when the request matches the first-load bootstrap (last 30 days, page 1, All, no search). */
export function matchesSTOrderDetailsDefaultBootstrapRequest(request, pageSize) {
    var _a, _b, _c, _d, _e, _f;
    if (pageSize === void 0) { pageSize = ST_ORDER_DETAILS_DEFAULT_ROWS_PER_PAGE; }
    var keys = getSTOrderDetailsDefaultDateRangeApiKeys();
    var tab = String((_a = request.activeTab) !== null && _a !== void 0 ? _a : "All").trim().toLowerCase();
    return (String((_b = request.dateFrom) !== null && _b !== void 0 ? _b : "") === keys.dateFrom &&
        String((_c = request.dateTo) !== null && _c !== void 0 ? _c : "") === keys.dateTo &&
        ((_d = request.pageNumber) !== null && _d !== void 0 ? _d : 1) === 1 &&
        ((_e = request.pageSize) !== null && _e !== void 0 ? _e : pageSize) === pageSize &&
        tab === "all" &&
        !String((_f = request.searchQuery) !== null && _f !== void 0 ? _f : "").trim());
}
